import { DatePipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateModule } from '@ngx-translate/core';
import moment from 'moment';
import { MONTHS } from '../../../../constants/agenda-view.constants';
import { DATE_FORMAT, DATE_ISO_FORMAT } from '../../../../constants/date.constants';
import { PreDefinedSlotAgendaFilteredData, PreDefinedSlotDao } from '../../../../db-models/pre-defined-slot-dao';
import { UserState } from '../../../../models/state.model';
import { DateUtilsService } from '../../../../services/date-utils.service';
import { TimezoneService } from '../../../../services/timezone.service';
import { KeepOrderKeyvaluePipe } from '../../../../shared/pipes/keep-order-keyvalue.pipe';
import { TranslationPipe } from '../../../../shared/pipes/translation.pipe';

const pipes = [KeepOrderKeyvaluePipe, DatePipe, TranslationPipe];
const modules = [TranslateModule, FontAwesomeModule];
const common = [NgClass, NgTemplateOutlet];

@Component({
  selector: 'app-pre-defined-slot-list',
  imports: [...common, ...modules, ...pipes],
  templateUrl: './pre-defined-slot-list.component.html',
  styleUrl: './pre-defined-slot-list.component.scss'
})
export class PreDefinedSlotListComponent {

  @Input() set slots(value: PreDefinedSlotDao[]) {
    this.selectedMonth = undefined;
    this._allSlots = value || [];
    this.prepareObject(this._allSlots);
  }
  @Input() loading = false;
  @Input() selectedSlot: PreDefinedSlotDao;
  @Input() protected userState: UserState;
  @Input() lang: string;

  @Output() slotSelected = new EventEmitter<PreDefinedSlotDao>();
  @Output() nextClicked = new EventEmitter<void>();

  private dateUtilsService = inject(DateUtilsService);
  private timezoneService = inject(TimezoneService);

  protected readonly monthsConst = MONTHS;
  protected readonly dateFormat = DATE_FORMAT;
  protected readonly skeletonMonths = Array(5);
  protected readonly skeletonSlots = Array(2);

  protected get timeFormat(): string {
    return this.dateUtilsService.userTimeFormat.date;
  }

  protected _filteredSlots: PreDefinedSlotAgendaFilteredData = {};
  protected _allSlots: PreDefinedSlotDao[] = [];
  protected selectedMonth: number;
  protected currentYear: number;
  protected selectedYear: number;
  protected maxYear: number;

  constructor() {
    const now = new Date();
    this.currentYear = now.getFullYear();
    this.selectedYear = this.currentYear;
    this.maxYear = this.currentYear + 2;
  }

  private prepareObject(slots: PreDefinedSlotDao[]): void {
    const timezone = this.userState?.timezone || this.timezoneService.getLocalTimezone();
    const filteredData: PreDefinedSlotAgendaFilteredData = {};

    const sortedSlots = [...slots].sort(
      (a, b) => new Date(a.start_utc).getTime() - new Date(b.start_utc).getTime()
    );

    sortedSlots.forEach(slot => {
      const slotMoment = moment.utc(slot.start_utc).tz(timezone);
      const slotYear = slotMoment.get('year');

      if (slotYear !== this.selectedYear) {
        return;
      }

      const monthIndex = slotMoment.get('month') + 1;
      const slotDate = slotMoment.format(DATE_ISO_FORMAT);

      if (!filteredData[monthIndex]) {
        filteredData[monthIndex] = {};
      }

      if (!filteredData[monthIndex][slotDate]) {
        filteredData[monthIndex][slotDate] = [];
      }

      filteredData[monthIndex][slotDate].push(slot);
    });

    this._filteredSlots = filteredData;
  }

  protected filterWithMonth(month: number): void {
    this.selectedMonth === month ? this.selectedMonth = null : this.selectedMonth = month;
  }

  protected getNextYearSlots(): void {
    if (this.selectedYear < this.maxYear) {
      this.selectedYear++;
      this.selectedMonth = undefined;
      this.prepareObject(this._allSlots);
    }
  }

  protected getPreviousYearSlots(): void {
    if (this.selectedYear > this.currentYear) {
      this.selectedYear--;
      this.selectedMonth = undefined;
      this.prepareObject(this._allSlots);
    }
  }

  protected isSlotSelected(slotUuid: string): boolean {
    return this.selectedSlot?.uuid === slotUuid;
  }

  protected onSelectSlot(slot: PreDefinedSlotDao): void {
    this.slotSelected.emit(slot);
  }

  protected hasNoSlots(): boolean {
    return Object.keys(this._filteredSlots).length === 0;
  }
}
